import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChartPie,
  CircleCheck,
  CirclePause,
  Copy,
  FileDown,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import Button from '../../components/Button'
import { useOnChange, useRemembered } from '../../components/listMemory'
import useListParams, { usePageParam } from '../../components/useListParams'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import DateRangeField from '../../components/DateRangeField'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import CampaignFormDrawer from './CampaignFormDrawer'
import {
  CAMPAIGN_SORTABLE,
  campaignState,
  deleteCampaign,
  formatCampaignDate,
  getCampaignSummary,
  getCampaignsPage,
  listCampaigns,
  saveCampaign,
  setCampaignActive,
} from './phishingService'
import { campaignsStrings as S } from './strings'
import './phishing.css'
import { serverDetail, serverText } from '../../lib/i18n'
import { downloadTablePdf, escapeHtml } from '../../lib/tablePdf'
import { chartToImage } from '../../lib/chartImage'
import { displayDate } from '../../lib/dates'
import {
  barSeries,
  categoryAxis,
  funnelOption,
  grid,
  legend,
  palette,
  valueAxis,
} from '../../components/chartTheme'

/**
 * حملات الإصطياد — Views/PhishingCampaigns/Index.cshtml.
 *
 * The five outcome columns are a funnel: every user counted at one stage was also
 * counted at the ones before it, so the numbers only ever fall left to right.
 * `تم التبليغ` is the good outcome and gets the success tone; `ادخل بيانات` is the
 * worst and gets the danger tone.
 */
/** The four derived states, in the order a campaign passes through them. */
const STATE_TONE = { scheduled: 'info', running: 'primary', finished: 'success', inactive: 'dark' }

function CampaignCountdown({ startAtUtc, now }) {
  const target = Date.parse(startAtUtc)
  if (!Number.isFinite(target)) return null
  const seconds = Math.max(0, Math.ceil((target - now) / 1000))
  if (seconds === 0) return <small className="ph__campaign-countdown" aria-live="polite">{S.review.waiting}</small>
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainder = seconds % 60
  const duration = days > 0 ? `${days}${S.review.daySymbol} ${hours}${S.review.hourSymbol}`
    : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
  return <small className="ph__campaign-countdown" aria-live="polite">{S.review.countdown} <bdi dir="ltr">{duration}</bdi></small>
}

/**
 * THE DATE RANGE IS AN ADDRESS, not a working position: it moves every figure on the
 * screen, so `?from=2026-01-01&to=2026-03-31` IS the quarter's funnel and can be sent to
 * someone else or reloaded onto the same numbers. Empty means all time and stays out of
 * the URL. Module-level, as `useListParams` requires.
 */
const RANGE_PARAMS = { from: '', to: '' }

export default function CampaignsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [exporting, setExporting] = useState(false)
  const [query, setQuery] = useRemembered('phishing-campaigns:query', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one — without this the table renders its "no
     records" state for a server error, which is the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [rangeParams, setRange] = useListParams(RANGE_PARAMS)
  /* Rebuilt from the two strings rather than used as it comes: `useListParams` hands back
     a fresh object whenever ANY parameter changes, the page included, and the summary
     below is fetched off this identity — so paging would refetch the funnel. */
  const range = useMemo(
    () => ({ from: rangeParams.from, to: rangeParams.to }),
    [rangeParams.from, rangeParams.to],
  )
  const [pendingDelete, setPendingDelete] = useState(null)
  const [pendingToggle, setPendingToggle] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [cloning, setCloning] = useState(false)
  const [options, setOptions] = useState({})
  const [summary, setSummary] = useState(null)
  /* In the address, so Back and a refresh both land on the same page. */
  const [page, setPage] = usePageParam()
  /* How many rows the reader chose to see — not an address, but it was lost on every
     visit to a campaign, so a list set to 100 came back at 25. */
  const [pageSize, setPageSize] = useRemembered('phishing-campaigns:pageSize', 25)
  /* The order, owned here with the page: the list is server-sorted. Null is the handler's
     own order — latest updated first — which no column shows, so no arrow is drawn. */
  const [sort, setSort] = useRemembered('phishing-campaigns:sort', null)
  const [total, setTotal] = useState(0)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [statusReloadKey, setStatusReloadKey] = useState(0)
  const [clockMs, setClockMs] = useState(() => Date.now())
  const silentReload = useRef(false)

  useEffect(() => {
    const timer = window.setInterval(() => setClockMs(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  /* A countdown changing to zero only changes pixels; it cannot know that Jobs has now
     persisted ActualStartedAt. Reload once at the scheduled instant, then every five
     seconds while a due campaign is waiting for the worker. Running campaigns refresh at
     a lower rate so their final state and funnel counts arrive without a browser refresh. */
  useEffect(() => {
    const now = Date.now()
    const scheduled = rows.filter((row) => campaignState(row) === 'scheduled')
    const running = rows.some((row) => campaignState(row) === 'running')
    if (scheduled.length === 0 && !running) return undefined

    const futureStarts = scheduled
      .map((row) => Date.parse(row.startAtUtc))
      .filter((time) => Number.isFinite(time) && time > now)
    const hasDueCampaign = scheduled.some((row) => {
      const time = Date.parse(row.startAtUtc)
      return Number.isFinite(time) && time <= now
    })
    const waits = []
    if (hasDueCampaign) waits.push(5_000)
    if (running) waits.push(15_000)
    if (futureStarts.length > 0) waits.push(Math.max(1_000, Math.min(...futureStarts) - now + 1_000))
    if (waits.length === 0) return undefined

    const timer = window.setTimeout(() => {
      silentReload.current = true
      setStatusReloadKey((key) => key + 1)
    }, Math.min(...waits))
    return () => window.clearTimeout(timer)
  }, [rows])

  const openForm = useCallback((campaign, clone = false) => {
    setEditing(campaign ?? null)
    setCloning(clone)
    setFormOpen(true)
  }, [])

  const refresh = useCallback(() => {
    if (refreshing) return
    setRefreshing(true)
    silentReload.current = true
    setReloadKey((key) => key + 1)
  }, [refreshing])

  /* The lookups the form chooses from, and the summary the count columns come from. Loaded
     once — none of it changes while the list is paged. */
  useEffect(() => {
    let cancelled = false
    getCampaignsPage()
      .then((data) => {
        if (cancelled) return
        const { summary: loaded, ...lists } = data
        setOptions(lists)
        setSummary(loaded)
      })
      .catch((error) => !cancelled && toast.error(serverDetail(error?.message) || S.fail.page))
    return () => {
      cancelled = true
    }
  }, [reloadKey, toast])

  /* A new search is a new list, so it starts at its first page — on a CHANGE only, or the
     page carried in the address would be thrown away on arrival. The table cannot do this
     itself: the list is SERVER-paged, so the page belongs to this screen. */
  useOnChange(() => setPage(1), [query])

  useEffect(() => {
    let cancelled = false
    const quiet = silentReload.current
    silentReload.current = false
    if (!quiet) setLoading(true)
    listCampaigns({ page, pageSize, query, sort })
      .then((response) => {
        if (cancelled) return
        setLoadError(null)
        setRows(response.rows)
        setTotal(response.total)
        setLoading(false)
        setRefreshing(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setTotal(0)
        setLoading(false)
        setRefreshing(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })
    return () => {
      cancelled = true
    }
  }, [page, pageSize, query, sort, reloadKey, statusReloadKey, toast])

  /* THE DATE RANGE NARROWS THE SUMMARY, not the list. `/summary` is the only route that takes
     one — `/filter` has no date parameter at all — so the range moves the counts and the
     totals, which is what a range over a funnel means. */
  useEffect(() => {
    if (!range.from && !range.to) return undefined
    let cancelled = false
    getCampaignSummary(range)
      .then((loaded) => !cancelled && setSummary(loaded))
      .catch((error) => !cancelled && toast.error(serverDetail(error?.message) || S.fail.summary))
    return () => {
      cancelled = true
    }
  }, [range, toast])

  /* Status polling is intentionally lighter than a full page reload, but the funnel should
     still move while a campaign is running. Keep it quiet: a transient background failure
     must not raise a toast every few seconds; the visible manual refresh remains available. */
  useEffect(() => {
    if (statusReloadKey === 0) return undefined
    let cancelled = false
    getCampaignSummary(range)
      .then((loaded) => !cancelled && setSummary(loaded))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [statusReloadKey, range])

  /* The row carries no counts — the campaign DTO has none — so they are merged in from the
     summary by id, the way the groups list merges its member counts. */
  const visibleRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        ...(summary?.byCampaign?.get(row.id) ?? {
          sent: null,
          opened: null,
          clicked: null,
          submitted: null,
          reported: null,
        }),
      })),
    [rows, summary],
  )

  /* Memoised because the column definitions close over it. */
  /* Both halves are named by the caller: an action that can succeed can fail, and the two
     sentences are about the same operation. It used to take only the success line and fall
     back to "تعذر إتمام العملية" for every failure. */
  const run = useCallback(
    async (action, done, failed) => {
      const outcome = await action
      if (!outcome.ok) return toast.error(serverDetail(outcome.message) || failed)
      toast.success(serverText(outcome.message) || done)
      setReloadKey((key) => key + 1)
      return undefined
    },
    [toast],
  )

  const save = async (values) => {
    if (saving) return undefined
    setSaving(true)
    try {
      // A clone goes through create validation, including the server's unique-name check.
      const outcome = await saveCampaign({ ...values, id: cloning ? 0 : (editing?.id ?? 0) })
      if (!outcome.ok) {
        toast.error(serverDetail(outcome.message) || (cloning ? S.fail.copy : S.fail.save))
        return false
      }
      /* The result is what matters after the final review. "Campaign copied" is wrong when
         that new copy was also launched, and the server's generic "saved" does not tell an
         administrator whether the worker will start sending. */
      const completion = values.launchAction ? S.completed[values.launchAction] : null
      toast.success(completion || serverText(outcome.message) || S.saved)
      setFormOpen(false)
      setReloadKey((key) => key + 1)
      return true
    } catch (error) {
      toast.error(serverDetail(error?.message) || (cloning ? S.fail.copy : S.fail.save))
      return false
    } finally {
      setSaving(false)
    }
  }

  /*
   * THE LIST AS IT IS ON SCREEN, as a PDF.
   *
   * The screen used to offer an `ExportMenu` with two formats and `onExport={() => {}}`
   * behind it — a control that opened, listed Excel and PDF, and did nothing whichever was
   * chosen. There is still no server export for this list (the phishing exports live on the
   * report slice, per campaign), so the sheet is built in the browser from the rows already
   * fetched. `lib/tablePdf.js` explains why it is rasterised rather than drawn as text.
   *
   * IT EXPORTS `visibleRows` — what the search and the date range have left — because the
   * button sits in the filter bar, beside those controls. Exporting the unfiltered set from
   * there would quietly contradict them.
   */
  const exportPdf = useCallback(async () => {
    if (visibleRows.length === 0) return toast.error(S.exportNothing)

    setExporting(true)
    try {
      /* DATES ONLY, STACKED. `formatCampaignDate` carries a time as well, and two of those
         side by side wrapped to three lines — which grew the row past the height the
         pagination counts in. A printed list wants the days; the times are on screen. */
      const day = (value) => String(formatCampaignDate(value) ?? '').slice(0, 10)
      const period = (row) => {
        const start = day(row.startDate)
        const end = day(row.endDate)
        if (!start && !end) return '—'
        return `<span dir="ltr" class="tpdf-num">${start}${
          end ? `<br>— ${end}` : ''
        }</span>`
      }
      const figure = (n) => `<span class="tpdf-num">${n ?? 0}</span>`

      /*
       * TWO CHARTS, AND THEY ANSWER DIFFERENT QUESTIONS.
       *
       * The FUNNEL is the phishing story across everything in view: of the mail that went
       * out, how much was opened, how much was clicked, how much gave up credentials. Each
       * stage is a subset of the one above it, which is the one shape a funnel is honest
       * about — and it is the figure a report is commissioned to show.
       *
       * The COLUMNS break the same four figures out per campaign, because the funnel
       * deliberately loses which campaign contributed what. Reported sits alongside rather
       * than in the funnel: it is not a stage anyone passes through on the way to being
       * compromised, it is the opposite outcome.
       *
       * Drawn by ECharts to PNGs (see `lib/chartImage`) rather than photographed with the
       * page, so their resolution does not depend on the sheet capture.
       */
      const sum = (key) => visibleRows.reduce((total, row) => total + (Number(row[key]) || 0), 0)
      const colours = palette()

      /* NOTHING HAS HAPPENED YET IS NOT A CHART. Campaigns that are all still scheduled
         produce a funnel of four equal empty bands and a column chart with a 0–1 axis and
         no columns — which reads as a broken report rather than as "no activity". A
         sentence says it, and the table underneath still lists what is scheduled. */
      const activity = ['sent', 'opened', 'clicked', 'submitted', 'reported'].reduce(
        (total, key) => total + sum(key),
        0,
      )

      const charts = activity === 0 ? [] : [
        {
          title: S.exportFunnel,
          height: 250,
          image: chartToImage(
            funnelOption([
              { label: S.columns.sent, value: sum('sent') },
              { label: S.columns.opened, value: sum('opened') },
              { label: S.columns.clicked, value: sum('clicked') },
              { label: S.columns.submitted, value: sum('submitted') },
            ]),
            { width: 900, height: 300 },
          ),
        },
        {
          title: S.exportByCampaign,
          height: 280,
          image: chartToImage(
            {
              legend: { ...legend, top: 0 },
              grid: { ...grid, top: 44, bottom: 60 },
              xAxis: (() => {
                const axis = categoryAxis(visibleRows.map((row) => row.name), { maxLabel: 14 })
                return { ...axis, axisLabel: { ...axis.axisLabel, interval: 0, hideOverlap: false } }
              })(),
              yAxis: valueAxis,
              /* THE COLOURS ARE THE SEVERITY. Opening a phishing mail is not a failure —
                 it is the neutral fact that it arrived — so it takes the quiet tone.
                 Clicking the link is the step that should worry someone, submitting
                 credentials is the loss, and reporting it is the win. Grey, amber, red,
                 green, in that order; the ramp is the finding. */
              series: [
                barSeries(S.columns.opened, visibleRows.map((r) => r.opened ?? 0), colours.absent),
                barSeries(S.columns.clicked, visibleRows.map((r) => r.clicked ?? 0), colours.incomplete),
                barSeries(S.columns.submitted, visibleRows.map((r) => r.submitted ?? 0), colours.failure),
                barSeries(S.columns.reported, visibleRows.map((r) => r.reported ?? 0), colours.success),
              ],
            },
            { width: 900, height: 340 },
          ),
        },
      ]

      await downloadTablePdf({
        charts,
        title: S.title,
        /* Only when something actually narrowed the list — an empty line under the
           heading says the sheet was filtered when it was not. */
        subtitle: [
          query ? `${S.exportSearch}: ${query}` : '',
          range.from || range.to ? `${range.from || '…'} — ${range.to || '…'}` : '',
        ]
          .filter(Boolean)
          .join('   ·   '),
        fileName: `phishing-campaigns-${new Date().toLocaleDateString('en-CA')}`,
        labels: {
          generatedOn: S.exportGeneratedOn,
          empty: S.exportEmpty,
          note: activity === 0 ? S.exportNoActivity : '',
        },
        columns: [
          {
            header: S.columns.name,
            width: '24%',
            /* The template under the name, as the table shows it — it is the one piece of
               context that makes two similarly-named campaigns tellable apart. */
            value: (row) =>
              `<strong>${escapeHtml(row.name)}</strong>${
                row.template
                  ? `<br><span style="color:#71717a;font-size:10px">${escapeHtml(row.template)}</span>`
                  : ''
              }`,
          },
          { header: S.columns.period, width: '17%', value: period },
          { header: S.columns.sent, align: 'center', value: (row) => figure(row.sent) },
          { header: S.columns.opened, align: 'center', value: (row) => figure(row.opened) },
          { header: S.columns.clicked, align: 'center', value: (row) => figure(row.clicked) },
          { header: S.columns.submitted, align: 'center', value: (row) => figure(row.submitted) },
          { header: S.columns.reported, align: 'center', value: (row) => figure(row.reported) },
          { header: S.columns.status, align: 'center', value: (row) => escapeHtml(S.states[campaignState(row)]) },
        ],
        rows: visibleRows,
      })
      toast.success(S.exported)
    } catch (error) {
      toast.error(serverDetail(error?.message) || S.exportFailed)
    } finally {
      setExporting(false)
    }
  }, [visibleRows, query, range, toast])

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: S.columns.name,
        width: '260px',
        render: (row) => (
          <span className="ph__campaign">
            <bdi className="ph__campaign-name">{row.name}</bdi>
            {row.template && <bdi className="ph__campaign-template">{row.template}</bdi>}
          </span>
        ),
      },
      {
        key: 'startDate',
        header: S.columns.period,
        value: (row) => {
          const start = displayDate(row.startDate)
          const end = displayDate(row.endDate)
          return start || end ? `${start} — ${end}` : ''
        },
        /* Each date is one unbreakable run; only the pair may split. Left to the
           browser the line broke inside `2026-07-23`, at its own hyphens. */
        render: (row) => {
          const start = displayDate(row.startDate)
          const end = displayDate(row.endDate)
          if (!start && !end) return '—'
          return (
            <span className="ph__period" dir="ltr">
              {start && <span className="ph__date">{start}{end ? ' —' : ''}</span>}
              {end && (
                <>
                  {' '}
                  <span className="ph__date">{end}</span>
                </>
              )}
            </span>
          )
        },
      },
      { key: 'sent', header: S.columns.sent, align: 'center' },
      { key: 'opened', header: S.columns.opened, align: 'center' },
      { key: 'clicked', header: S.columns.clicked, align: 'center' },
      {
        key: 'submitted',
        header: S.columns.submitted,
        align: 'center',
        /* Nobody fell for it is the BEST outcome on this column, and a red 0
           reads as an alarm over exactly that. A zero carries no signal either
           way here, so it steps back instead of wearing a status colour. */
        render: (row) => (
          <span className={row.submitted > 0 ? 'ph__bad' : 'ph__nil'}>{row.submitted}</span>
        ),
      },
      {
        key: 'reported',
        header: S.columns.reported,
        align: 'center',
        render: (row) => (
          <span className={row.reported > 0 ? 'ph__good' : 'ph__nil'}>{row.reported}</span>
        ),
      },
      {
        key: 'isActive',
        header: S.columns.status,
        /* FOUR STATES, DERIVED. A campaign has no status column — not on the DTO and not in
           the database — so it is read from the three facts it does carry: whether it is
           active at all, whether it has actually started, and whether it has finished. */
        value: (row) => S.states[campaignState(row)],
        render: (row) => {
          const state = campaignState(row)
          return <span className="ph__campaign-state">
            <StatusBadge tone={STATE_TONE[state]}>{S.states[state]}</StatusBadge>
            {state === 'scheduled' && <CampaignCountdown startAtUtc={row.startAtUtc} now={clockMs} />}
          </span>
        },
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '112px',
        sortable: false,
        searchable: false,
        /* Six glyphs held this column open at 230px, which was most of the 337px
           that pushed the list into horizontal scroll. The report and the edit
           lead; the other four keep their icons and gain a visible label. */
        render: (row) => (
          <RowActions
            actions={[
              /* The report screen exists now. `التنبيهات` has no endpoint anywhere in this
                 slice, so it is REMOVED rather than left declared — a row action that
                 opens nothing is the thing this sweep exists to take out. COSMETIC_UI.md. */
              {
                id: 'report',
                icon: ChartPie,
                label: S.row.report,
                tone: 'info',
                onClick: () => navigate(`/phishing/report/${row.id}`),
              },
              { id: 'edit', icon: Pencil, label: S.edit, onClick: () => openForm(row) },
              {
                id: 'active',
                /* `Ban` — the crossed circle — is the mark for FORBIDDEN, and stopping a
                   campaign is not that; it is the same pause every other list in the panel
                   offers, and it is what the status chip on this row already shows. */
                icon: row.isActive ? CirclePause : CircleCheck,
                label: row.isActive ? S.row.disable : S.row.activate,
                tone: row.isActive ? 'warning' : 'success',
                onClick: () => setPendingToggle(row),
              },
              {
                id: 'copy',
                icon: Copy,
                label: S.row.copy,
                tone: 'primary',
                onClick: () => openForm(row, true),
              },
              {
                id: 'delete',
                icon: Trash2,
                label: S.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [navigate, openForm, clockMs],
  )
  /* Only what `FilterPhishingCampaignsHandler` can order by draws an arrow; the funnel
     counts are fetched per row after the page arrives. */
  const sortableColumns = useMemo(
    () => columns.map((column) => ({ ...column, sortable: CAMPAIGN_SORTABLE.includes(column.key) })),
    [columns],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        count={loading ? undefined : total}
        actions={
          <>
            <Button variant="soft" onClick={refresh} busy={refreshing}>
              <RefreshCw size={16} strokeWidth={1.75} />
              {S.refresh}
            </Button>
            <Button onClick={() => openForm(null)}>
              <Plus size={16} strokeWidth={2} />
              {S.addNew}
            </Button>
          </>
        }
      />

      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: S.columns.name }}
        chips={
          range.from || range.to
            ? [
                {
                  id: 'range',
                  label: S.report,
                  value: `${range.from || '…'} — ${range.to || '…'}`,
                  onRemove: () => setRange({ from: '', to: '' }),
                },
              ]
            : []
        }
        /* ONE FORMAT, SO ONE BUTTON. A menu that opens to reveal a single item is a
           click spent on a question with one answer. */
        actions={
          <Button variant="ghost" onClick={exportPdf} disabled={exporting}>
            <FileDown size={16} strokeWidth={1.75} />
            {exporting ? S.exporting : S.exportPdf}
          </Button>
        }
      >
        <DateRangeField
          value={range}
          onChange={setRange}
          labels={{ from: S.from, to: S.to }}
        />
      </FilterBar>

      <DataTable
        error={loadError}
        columns={sortableColumns}
        rows={visibleRows}
        loading={loading}
        query={query}
        getRowId={(row) => row.id}
        rowTo={(row) => `/phishing/report/${row.id}`}
        server={{
          page,
          pageSize,
          total,
          sort,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
            setSort(next.sort)
          },
        }}
      />

      <CampaignFormDrawer
        open={formOpen}
        campaign={editing}
        cloning={cloning}
        {...options}
        onClose={() => !saving && setFormOpen(false)}
        saving={saving}
        onSave={save}
      />

      {/* `استيراد` IS GONE — see `COSMETIC_UI.md`, "Phishing campaigns — import".
          There is no campaign import endpoint: the modal took a file, sent nothing, and
          raised the DELETE confirmation as its success message. Its template link
          (`/root/ExcelTemplates/Phishing.xlsx`) 404s as well — the file has never existed. */}

      <ConfirmDialog
        open={!!pendingDelete}
        message={`${S.deleteConfirm} ${S.deleteWarning}`}
        confirmLabel={S.delete}
        cancelLabel={S.close}
        onConfirm={() => {
          const campaign = pendingDelete
          setPendingDelete(null)
          run(deleteCampaign(campaign.id), S.deleted, S.fail.delete)
        }}
        onCancel={() => setPendingDelete(null)}
      />

      {/* DELETING A CAMPAIGN DESTROYS ITS REPORT. The server removes its departments, its
          users and every recorded event with no guard for one that has already run, so a
          finished campaign's results go with it — hence a confirm that says so. */}
      <ConfirmDialog
        open={!!pendingToggle}
        title={pendingToggle?.isActive ? S.disableTitle : S.activateTitle}
        tone={pendingToggle?.isActive ? 'warning' : 'success'}
        message={pendingToggle?.isActive ? S.disableConfirm : S.activateConfirm}
        confirmLabel={pendingToggle?.isActive ? S.row.disable : S.row.activate}
        cancelLabel={S.close}
        onConfirm={() => {
          const campaign = pendingToggle
          setPendingToggle(null)
          run(
            setCampaignActive(campaign.id, !campaign.isActive),
            campaign.isActive ? S.disabled : S.activated,
            campaign.isActive ? S.fail.disable : S.fail.activate,
          )
        }}
        onCancel={() => setPendingToggle(null)}
      />
    </>
  )
}
